import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetSliders } from "../../hooks/sliders/useGetSliders";
import { useDeleteSlider } from "../../hooks/sliders/useDeleteSlider";
import { useMassDestroySliders } from "../../hooks/sliders/useMassDestroySliders";
import { useMassRestoreSliders } from "../../hooks/sliders/useMassRestoreSliders";
import { useMassForceDeleteSliders } from "../../hooks/sliders/useMassForceDeleteSliders";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import SlidersTableRow from "./Table/SlidersTableRow";
import { SlidersTableColumns } from "./Table/SlidersTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const SlidersTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-slider"),
            create: has("create-slider"),
            edit: has("update-slider"),
            delete: has("delete-slider"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");

    // CRUD Hooks
    const { mutate: deleteSliderMutate, isPending: isDelete } = useDeleteSlider();
    const { mutate: massDestroySlidersMutate, isPending: isDeleteAll } = useMassDestroySliders();
    const { mutate: massRestoreSlidersMutate, isPending: isRestoreAll } = useMassRestoreSliders();
    const { mutate: massForceDeleteSlidersMutate, isPending: isForceDeleteAll } = useMassForceDeleteSliders();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/sliders",
        deleteSingle: deleteSliderMutate,
        deleteAll: massDestroySlidersMutate,
        restoreSingle: (id, options) => massRestoreSlidersMutate({ items: [id] }, options),
        restoreAll: massRestoreSlidersMutate,
        forceDeleteSingle: (id, options) => massForceDeleteSlidersMutate({ items: [id] }, options),
        forceDeleteAll: massForceDeleteSlidersMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at',
        }),
        [page, perPage, search, trashedFilter]
    );

    const { isLoading, error, data } = useGetSliders(params);
    const sliders = data?.data || [];
    const meta = data?.meta || {};

    // تجهيز الفلاتر لـ TableLayout
    // No additional filters besides archive
    const filters = [];

    const columns = useMemo(() => SlidersTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "السلايدر", en: "Sliders" }}
            columns={columns}
            items={sliders}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, sliders)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/sliders/create")}
            addButtonLabel={{ ar: "إضافة سلايدر", en: "Add Slider" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <SlidersTableRow
                    {...params}
                    key={params.item.id}
                    slider={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default SlidersTable;